import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getPaperTexture } from '../paper-texture.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { onArtworkTransitionStart } from '../artwork-transition.js';

// Shared-rig contract (see the framing addendum): ink on paper, lit by nothing.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'halftone',
  family: 'paper',
  energyLevel: 0.35,
  bpmMin: 50,
  bpmMax: 160,
  movement: 0.30,
  danceability: 0.30,
  tonality: 0.70,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.65] },
  enabled: true
};

// Halftone: the cover printed as a four-colour screen, dot by dot.
//
// The library already treats the cover as an IMAGE - kaleido-drift
// folds it. It does not treat
// it as a MATERIAL. Here it is a physical impression:
// four ink screens at the printer's own angles, laid on paper, each breathing
// at its own rate so the registration drifts the way a press drifts.
//
// Third of the paper family after sumi.js and the map that briefly preceded
// this one: dark pigment on a light ground, NORMAL blending, nothing bright
// enough for the bloom pass to find.

// The printer's angles. These four are not decorative - they are the specific
// set that keeps the four screens from beating against each other, and the
// rosette that appears where they overlap is the visual proof they are right.
const ANGLE_CYAN = 15;
const ANGLE_MAGENTA = 75;
const ANGLE_YELLOW = 0;
const ANGLE_BLACK = 45;

// Real process inks, not saturated primaries - printed cyan is not (0,1,1),
// and using the pure primary is the single thing that makes a halftone read as
// a computer effect rather than as ink.
const INK_CYAN = 0x1a9fd4;
const INK_MAGENTA = 0xd41a7f;
const INK_YELLOW = 0xf0c419;
const INK_BLACK = 0x1a1614;

const PAPER_FALLBACK_COLOR = 0xf2ede2;
// Everything in the frame stays under this. The bloom pass threshold is 0.78
// and it is evaluated after tone mapping, so a shader writing 0.74 arrives
// higher than 0.74 - measured on the previous paper piece, where a nominal
// 0.72 still lit the pass. This is set from that measurement, not from the
// nominal figure.
// 0.50, not the brief's 0.74, and the difference is a colour-space one rather
// than a disagreement. The shader writes LINEAR values; the frame is converted
// to sRGB on output, which lifts 0.74 linear to about 0.88 on screen. Measured:
// a ceiling of 0.62 rendered a peak of 0.831. 0.74^2.2 = 0.517 is the linear
// value that actually ARRIVES at 0.74, so that is what is set here. The cost is
// real - the paper reads as a warm grey rather than a bright sheet.
const PAPER_CEILING = 0.45;

// Screen frequency, in cells per frame height. character.percussiveness drives
// it across this range: a dense, percussive track gets a fine screen, a sparse
// one a coarse graphic one.
const FREQ_MIN = 45;
const FREQ_MAX = 110;
// A cell narrower than this many device pixels aliases no matter how soft the
// dot edge is, so the frequency is capped rather than the setting trusted -
// "le controle doit etre dans le code, pas dans les reglages".
const MIN_CELL_PX = 3.0;

const DOT_SCALE = 0.62;      // cell fraction at full ink
// Each screen breathes on its own band, so the four are never in step and
// their overlaps keep changing - which is where the image stops looking like
// a filter and starts looking like a press that is slightly out of register.
const BREATHE_BLACK = 0.14;
const BREATHE_MID = 0.09;
const BREATHE_HIGH = 0.09;

// Angle drift across the track, peaking at the midpoint and returning.
const MISREGISTER_DEG = 1.5;

// WITHOUT AN ANALYSIS TIMELINE the screens have nothing to breathe on:
// director.bands falls back to a constant (see director.js's NEUTRAL_BANDS),
// every dot scale holds its value, and the print is a still image. Note the
// dependency is the TIMELINE, not the tempo - a track can have a perfectly
// good BPM from Deezer and still land here, because bands come from radiopi's
// onset analysis and that is what is missing.
//
// The substitute is three slow oscillators standing in for the three bands.
// Their periods are deliberately not multiples of one another, so the four
// screens keep drifting in and out of step with each other exactly as they do
// on real band data - which is the whole point of the effect. No pulse, no
// tempo, nothing beat-locked: this is a press running, not a metronome.
const IDLE_PERIODS = [7.3, 9.7, 12.9];
// The registration wander also stalls when there is no duration to take a
// progress from (web radio reports 0), so it gets its own slow cycle.
const IDLE_MISREGISTER_PERIOD = 41.0;

// The cover is a square laid over a wide frame at slightly more than frame
// height, so it overflows top and bottom and leaves a margin either side. The
// screen carries on into those margins and runs out of ink over FALLOFF of the
// width - the alternative being either a stretch or two empty bands.
const COVER_SQUARE_SCALE = 1.15;
const EDGE_FALLOFF = 0.15;

// Inverted safe zone, as on every paper piece: the dots THIN behind the cover
// so the overlay sits on a light screen. The result is the cover resting on a
// printed copy of itself. Shaped as a disc on the cover rather than the shared
// box over the whole overlay - see safeZoneDots.
const SAFE_ZONE_DOT_SCALE = 0.5;

const CROSSFADE_MS = 900;

const TIER_LAYERS = { 0: 2, 1: 3, 2: 4 };
const TIER_FREQ_SCALE = { 0: 0.7, 1: 0.88, 2: 1 };

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// One screen, written out in full. Not a loop and not a function called four
// times with a varying angle: the performance contract bans loops in a
// fragment shader this large, and every layer's angle is a compile-time
// constant anyway.
//
// TWO details here are the whole technique, and both were in the brief for
// good reason:
//   sqrt on the radius - the DOT'S AREA has to be proportional to ink density,
//     and area goes as the square of the radius. Without it the midtones come
//     out far too light and the whole print looks washed.
//   sampling at the CELL CENTRE, never at the current pixel - a dot must carry
//     ONE value across its whole area. Sample per-pixel and every dot gets its
//     own internal gradient, which is exactly the thing a screen replaces.
function screenLayer(name, angleExpr, channelExpr) {
  return `
    {
      float a = ${angleExpr};
      float ca = cos(a); float sa = sin(a);
      vec2 rot = vec2(p.x * ca - p.y * sa, p.x * sa + p.y * ca);
      vec2 cell = rot * uFrequency;
      vec2 f = fract(cell) - 0.5;
      vec2 centreRot = (floor(cell) + 0.5) / uFrequency;
      vec2 centre = vec2(centreRot.x * ca + centreRot.y * sa,
                        -centreRot.x * sa + centreRot.y * ca);
      float v = ${channelExpr};
      float radius = sqrt(max(v, 0.0)) * ${name}Scale;
      float cov = 1.0 - smoothstep(radius - uSoft, radius + uSoft, length(f));
      ${name}Cov = cov * edge;
    }
  `;
}

function buildFragmentShader(layers, hasPaper) {
  const wantYellow = layers >= 4;
  const wantColour = layers >= 3;

  return `
    precision mediump float;

    uniform sampler2D uArtwork;
    uniform sampler2D uArtworkPrev;
    uniform float uMix;
    uniform float uHasArtwork;
    uniform sampler2D uPaper;
    uniform float uHasPaper;
    uniform vec3 uPaperColor;
    uniform float uFrequency;
    uniform float uSoft;
    uniform float uAspect;
    uniform float uMisregister;
    uniform float uScaleK;
    uniform float uScaleCM;
    uniform float uScaleY;
    uniform vec3 uInkC;
    uniform vec3 uInkM;
    uniform vec3 uInkY;
    uniform vec3 uInkK;
    uniform vec3 uFallbackInk;
    uniform float uPatternPhase;

    uniform vec2 uSafeCenter;
    uniform vec2 uSafeHalfSize;
    uniform float uSafeFeather;

    varying vec2 vUv;

    // Density multiplier inside the cover's rectangle. INVERTED relative to
    // every additive visualizer: on paper, "attenuate" has to mean less ink,
    // not less light - dimming a dark dot on a light ground would brighten it
    // unevenly and read as a stain.
    float safeZoneDots(vec2 uv) {
      vec2 ndc = uv * 2.0 - 1.0;
      vec2 s = vec2(ndc.x * uAspect, ndc.y);
      // ROUND, AND ON THE COVER ALONE. uSafeHalfSize spans the whole overlay -
      // the cover plus the title and artist stacked under it - and the shared
      // box SDF thinned the ink across all of it, printing a pale rectangle
      // that hung below the artwork. The cover is a square at the TOP of that
      // box, so its half-size is the smaller of the box's two and its centre
      // sits that far down from the box's top edge. A disc around it reads as
      // a halo hugging the sleeve, which is what the other pieces do.
      float side = min(uSafeHalfSize.x, uSafeHalfSize.y);
      vec2 centre = vec2(uSafeCenter.x, uSafeCenter.y + uSafeHalfSize.y - side);
      float e = length(s - centre) / max(side, 0.001);
      return mix(${SAFE_ZONE_DOT_SCALE.toFixed(2)}, 1.0, smoothstep(0.88, 1.72, e));
    }

    // Sampled through the crossfade, so a track change is one dissolve between
    // two screens rather than two screens fighting.
    vec3 artworkAt(vec2 uv) {
      vec3 a = texture2D(uArtwork, uv).rgb;
      vec3 b = texture2D(uArtworkPrev, uv).rgb;
      return mix(b, a, uMix);
    }

    // No cover at all: a procedural print rather than an empty sheet. Two
    // screens only, and deliberately geometric - it should read as a printed
    // pattern, not as a failed photograph.
    vec3 fallbackAt(vec2 uv) {
      vec2 g = uv * vec2(uAspect, 1.0) * 3.0;
      float rings = 0.5 + 0.5 * sin(length(g - vec2(uAspect * 1.5, 1.5)) * 4.0 - uPatternPhase);
      float bars = 0.5 + 0.5 * sin((g.x + g.y) * 2.0 + uPatternPhase * 0.6);
      float v = mix(rings, bars, 0.4);
      return mix(vec3(1.0), uFallbackInk, v * 0.8);
    }

    void main() {
      // The cover as a centred square a little taller than the frame: it runs
      // off the top and bottom, and leaves a margin left and right for the
      // screen to run out of ink into.
      vec2 p = (vUv - vec2(0.5)) * vec2(uAspect, 1.0);
      vec2 artUv = p / ${COVER_SQUARE_SCALE.toFixed(3)} + vec2(0.5);

      // Ink running out toward the lateral edges. Distance past the square's
      // own side, as a fraction of the frame width.
      float halfSide = ${COVER_SQUARE_SCALE.toFixed(3)} * 0.5;
      float past = (abs(p.x) - halfSide) / uAspect;
      float edge = 1.0 - smoothstep(0.0, ${EDGE_FALLOFF.toFixed(3)}, max(past, 0.0));
      edge *= safeZoneDots(vUv);

      vec3 src = uHasArtwork > 0.5 ? artworkAt(clamp(artUv, 0.0, 1.0)) : fallbackAt(vUv);

      // Straight CMYK separation. k first, then the three inks relative to
      // what k already carries.
      float k = 1.0 - max(src.r, max(src.g, src.b));
      float ik = max(1.0 - k, 1e-3);
      float c = (1.0 - src.r - k) / ik;
      float m = (1.0 - src.g - k) / ik;
      float y = (1.0 - src.b - k) / ik;

      float kScale = uScaleK;
      float cScale = uScaleCM;
      float mScale = uScaleCM;
      float yScale = uScaleY;

      float kCov = 0.0; float cCov = 0.0; float mCov = 0.0; float yCov = 0.0;

      ${screenLayer('k', `radians(${ANGLE_BLACK.toFixed(1)}) + uMisregister`, 'k')}
      ${wantColour ? screenLayer('c', `radians(${ANGLE_CYAN.toFixed(1)}) - uMisregister`, 'c') : ''}
      ${wantColour ? screenLayer('m', `radians(${ANGLE_MAGENTA.toFixed(1)}) + uMisregister * 0.6`, 'm') : ''}
      ${wantYellow ? screenLayer('y', `radians(${ANGLE_YELLOW.toFixed(1)}) - uMisregister * 0.4`, 'y') : ''}
      ${layers === 2 ? screenLayer('c', `radians(${ANGLE_CYAN.toFixed(1)}) - uMisregister`, 'max(c, max(m, y))') : ''}

      vec3 paper = uHasPaper > 0.5 ? texture2D(uPaper, vUv).rgb : uPaperColor;
      paper = min(paper, vec3(${PAPER_CEILING.toFixed(3)}));

      // MULTIPLICATIVE, one screen after another. Ink absorbs light; stacking
      // it additively would make dense areas brighter than bare paper, which
      // is the exact opposite of what a press does.
      vec3 col = paper;
      col *= mix(vec3(1.0), uInkK, kCov);
      ${wantColour || layers === 2 ? 'col *= mix(vec3(1.0), uInkC, cCov);' : ''}
      ${wantColour ? 'col *= mix(vec3(1.0), uInkM, mCov);' : ''}
      ${wantYellow ? 'col *= mix(vec3(1.0), uInkY, yCov);' : ''}

      gl_FragColor = vec4(min(col, vec3(${PAPER_CEILING.toFixed(3)})), 1.0);
    }
  `;
}

const vertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // Fullscreen quad straight in clip space - immune to the scene's own
    // camera drift, which on a fixed print would read as the paper sliding.
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function makeBlankTexture(hex) {
  const c = new THREE.Color(hex);
  const data = new Uint8Array([
    Math.round(c.r * 255), Math.round(c.g * 255), Math.round(c.b * 255), 255
  ]);
  const t = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
}

export class HalftoneVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : 1;
    this._tier = TIER_LAYERS[tier] != null ? tier : 1;
    const forcedLayers = parseInt(urlParam('halftone'), 10);
    this._layers = (forcedLayers >= 1 && forcedLayers <= 4)
      ? forcedLayers
      : TIER_LAYERS[this._tier];
    this._forcedFreq = parseFloat(urlParam('freq'));
    this._noPaper = urlParam('nopaper') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    const paper = this._noPaper ? null : getPaperTexture();
    this._blank = makeBlankTexture(0x808080);

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uArtwork: { value: this._blank },
      uArtworkPrev: { value: this._blank },
      uMix: { value: 1 },
      uHasArtwork: { value: 0 },
      uPaper: { value: paper },
      uHasPaper: { value: paper ? 1 : 0 },
      uPaperColor: { value: new THREE.Color(PAPER_FALLBACK_COLOR) },
      uFrequency: { value: 70 },
      uSoft: { value: 0.08 },
      uMisregister: { value: 0 },
      uScaleK: { value: DOT_SCALE },
      uScaleCM: { value: DOT_SCALE },
      uScaleY: { value: DOT_SCALE },
      uInkC: { value: new THREE.Color(INK_CYAN) },
      uInkM: { value: new THREE.Color(INK_MAGENTA) },
      uInkY: { value: new THREE.Color(INK_YELLOW) },
      uInkK: { value: new THREE.Color(INK_BLACK) },
      uFallbackInk: { value: new THREE.Color(INK_CYAN) },
      uPatternPhase: { value: 0 }
    });

    this._geometry = new THREE.PlaneGeometry(1, 1);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: buildFragmentShader(this._layers, !!paper),
      blending: THREE.NormalBlending,
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._mesh = new THREE.Mesh(this._geometry, this._material);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = -1000;
    scene.add(this._mesh);

    this._time = 0;
    this._lastArtworkUrl = null;
    this._artworkTexture = null;
    this._prevTexture = null;
    this._crossfadeStart = null;
    this._pendingSwap = false;

    // The crossfade is driven by the SAME event the DOM cover fades on (see
    // artwork-transition.js), not by the track change - which happens a whole
    // download before the new cover exists. Keyed off it, the printed copy and
    // the real one change together.
    this._onTransition = () => { this._pendingSwap = true; };
    onArtworkTransitionStart(this._onTransition);
  }

  _loadArtwork(url) {
    loadArtworkTexture(
      url,
      { wrap: THREE.ClampToEdgeWrapping, mipmaps: true },
      (texture) => {
        this._nextTexture = texture;
        this._startCrossfade();
      },
      () => { /* keep printing whatever is on the sheet */ }
    );
  }

  _startCrossfade() {
    if (!this._nextTexture) return;
    const outgoing = this._prevTexture;
    this._prevTexture = this._artworkTexture;
    this._artworkTexture = this._nextTexture;
    this._nextTexture = null;
    this._uniforms.uArtwork.value = this._artworkTexture;
    this._uniforms.uArtworkPrev.value = this._prevTexture || this._artworkTexture;
    this._uniforms.uHasArtwork.value = 1;
    this._uniforms.uMix.value = this._prevTexture ? 0 : 1;
    this._crossfadeStart = this._prevTexture ? this._time : null;
    if (outgoing && outgoing !== this._artworkTexture) outgoing.dispose();
  }

  // Screen frequency in cells per frame height, capped so a cell can never get
  // narrower than MIN_CELL_PX device pixels. This is where 4K is handled: the
  // cap is computed from the real drawing-buffer height, so the same
  // character-driven frequency yields a coarser screen on a small panel and is
  // allowed to stay fine on a large one, and neither aliases.
  _resolveFrequency(density) {
    const base = FREQ_MIN + (FREQ_MAX - FREQ_MIN) * density;
    let freq = base * TIER_FREQ_SCALE[this._tier];
    if (this._forcedFreq > 0) freq = this._forcedFreq;
    const heightPx = (typeof window !== 'undefined')
      ? window.innerHeight * Math.min(window.devicePixelRatio || 1, 2)
      : 1080;
    const maxFreq = heightPx / MIN_CELL_PX;
    return Math.min(freq, maxFreq);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;

    if (this._uniforms.uPaper.value === null && !this._noPaper) {
      const paper = getPaperTexture();
      if (paper) {
        this._uniforms.uPaper.value = paper;
        this._uniforms.uHasPaper.value = 1;
      }
    }

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
    }
    // A decoded texture waits for the shared transition instant; if the event
    // has already fired for this track the swap happens as soon as it lands.
    if (this._pendingSwap && this._nextTexture) {
      this._pendingSwap = false;
      this._startCrossfade();
    }
    if (this._crossfadeStart !== null) {
      const t = (this._time - this._crossfadeStart) * 1000 / CROSSFADE_MS;
      this._uniforms.uMix.value = Math.min(1, Math.max(0, t));
      if (t >= 1) {
        this._crossfadeStart = null;
        if (this._prevTexture) { this._prevTexture.dispose(); this._prevTexture = null; }
        this._uniforms.uArtworkPrev.value = this._artworkTexture || this._blank;
      }
    }

    const director = profile.director;
    // hasReliableGrid is exactly "a timeline exists", which is exactly "bands
    // are real" - the same flag aurora.js keys its own fallback off.
    const live = !!(director && director.hasReliableGrid && director.bands);
    const t = this._time;
    const bands = live ? director.bands : {
      low: 0.5 + 0.5 * Math.sin(2 * Math.PI * t / IDLE_PERIODS[0]),
      mid: 0.5 + 0.5 * Math.sin(2 * Math.PI * t / IDLE_PERIODS[1] + 1.7),
      high: 0.5 + 0.5 * Math.sin(2 * Math.PI * t / IDLE_PERIODS[2] + 3.4)
    };
    const character = director ? director.character : null;
    // The brief asks for character.density. The timeline does not carry one -
    // it exposes dynamics and percussiveness (see track-analysis.js), and
    // percussiveness is precisely what adaptive-party.js already forwards to
    // the orchestrator UNDER the name density. Same number, named honestly
    // here rather than inventing a field that does not exist.
    const density = character && character.percussiveness != null
      ? character.percussiveness
      : 0.5;

    this._uniforms.uFrequency.value = this._resolveFrequency(density);

    // Cell size in uv units, converted to the soft edge width. Proportional to
    // the cell rather than constant, which is what keeps the dot edge exactly
    // one screen-pixel-ish soft at every frequency and resolution.
    const cellUv = 1 / this._uniforms.uFrequency.value;
    this._uniforms.uSoft.value = cellUv * 0.5 * 0.22;

    // Four screens, three different bands, so they never breathe together.
    this._uniforms.uScaleK.value = DOT_SCALE * (1 + (bands.low - 0.5) * 2 * BREATHE_BLACK);
    this._uniforms.uScaleCM.value = DOT_SCALE * (1 + (bands.mid - 0.5) * 2 * BREATHE_MID);
    this._uniforms.uScaleY.value = DOT_SCALE * (1 + (bands.high - 0.5) * 2 * BREATHE_HIGH);

    // Registration drifts out and comes back over the track - a press warming
    // up and being pulled straight again.
    // Registration: the arch across the track when there is a track to arch
    // across, a slow cycle when there is not (radio reports duration 0, so
    // progress is pinned at 0 and the arch would never leave the floor).
    let misregister;
    if (profile.duration > 0) {
      const progress = THREE.MathUtils.clamp(profile.progress, 0, 1);
      misregister = Math.sin(Math.PI * progress);
    } else {
      misregister = Math.sin(2 * Math.PI * t / IDLE_MISREGISTER_PERIOD);
    }
    this._uniforms.uMisregister.value =
      misregister * THREE.MathUtils.degToRad(MISREGISTER_DEG);

    this._uniforms.uAspect.value = this._camera.aspect;
    if (!paused) this._uniforms.uPatternPhase.value = this._time * 0.35;

    const palette = profile.artworkPalette;
    const vivid = palette && palette.vividColors;
    if (vivid && vivid.length) this._uniforms.uFallbackInk.value.set(vivid[0]);
  }

  dispose() {
    this._scene.remove(this._mesh);
    this._geometry.dispose();
    this._material.dispose();
    this._blank.dispose();
    if (this._artworkTexture) this._artworkTexture.dispose();
    if (this._prevTexture) this._prevTexture.dispose();
    if (this._nextTexture) this._nextTexture.dispose();
    this._artworkTexture = null;
    this._prevTexture = null;
    this._nextTexture = null;
  }
}
